<script lang="ts">
import { computed, defineComponent, inject, onActivated, onDeactivated, PropType, reactive, ref, watch } from "vue";
import Modal from "@/components/Modal.vue";
import formValidierung from "@/businesslogic/formValidierung";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Steps from "@/components/Steps.vue";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import Antragsteller from "@/components/person/Antragsteller.vue";
import Vorschaeden from "@/components/vorschaeden/Vorschaeden.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Zahlungsdaten from "@/components/zahlungsdaten/Zahlungsdaten.vue";
import Vorversicherung from "@/components/vorversicherung/Vorversicherung.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import HugMitgliedschaft from "@/components/hug/HugMitgliedschaft.vue";
import { NavvItem } from "@/model/NavItem";
import Logger from "js-logger";
import VertragsunterlagenDigital from "@/components/abschluss/VertragsunterlagenDigital.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "IhreDatenPHV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    VertragsunterlagenDigital,
    HugMitgliedschaft,
    Zeile,
    TitelZelle,
    Zelle,
    ValidierungsFehler,
    Antragsteller,
    Vorversicherung,
    Zahlungsdaten,
    Vorschaeden,
    Steps,
    Modal,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup: function (_props, { emit }) {
    const piniaStore = useFormStore();

    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const zeigeSpinner: any = inject("zeigeSpinner");

    const { formatCurrency } = formatUtils();
    const fehler = ref({} as any);
    const zeigeFrageHugNachlass = ref(false);
    let komponenteIstAktiv = false;

    const antragstellerRef = ref<InstanceType<typeof Antragsteller>>();
    const zahlungsdatenRef = ref<InstanceType<typeof Zahlungsdaten>>();

    const isHaeuslicheGemeinschaft = computed(() => {
      return piniaStore.state.personendaten.mitversichertePerson.artDerPartnerschaft === 2;
    });

    const modalBeitragsaenderung = reactive({
      titel: "Beitragsänderung",
      text: "Durch Ihre Änderung wurde der Beitrag angepasst. Bitte überprüfen Sie den neuen Beitrag.",
      hatSchliessenButton: true,
      schliessenButtonText: "OK",
    });
    let beitragsaenderungDurchSchadenanzahl = false;
    let beitragsaenderungDurchHugMitgliedschaft = false;

    function hatMitversichertePerson() {
      return (
        piniaStore.state.tarifinformationen.tarifVariante === "993" ||
        piniaStore.state.tarifinformationen.tarifVariante === "994"
      );
    }

    async function eingabenSindValide() {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereZusammengesetzteNamenLaenge } =
        formValidierung();
      await validiereForm();
      validiereZusammengesetzteNamenLaenge();

      let anstragstellerValide: boolean | undefined = true;
      if (getFormFehlerAnzahl() === 0) {
        anstragstellerValide = await antragstellerRef.value?.pruefen();
      }

      Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl(), anstragstellerValide, getFormFehler());
      fehler.value = getFormFehler();

      return anstragstellerValide && getFormFehlerAnzahl() === 0;
    }

    function anfragePruefenUndWeiter() {
      zeigeSpinner.value = true;
      AnfrageService.pruefen(piniaStore.getSessionToken(piniaStore.state), piniaStore.state, "IhreDatenPHV")
        .then((res) => {
          Logger.debug("anfragePruefenUndWeiter(): OK. Ergebnis: ", res.data);
          if (res.data && res.data.aussteuerung !== undefined) {
            piniaStore.state.isDunkelverarbeitung = res.data.aussteuerung === false;
          }
        })
        .catch((err) => {
          Logger.debug("anfragePruefenUndWeiter(): Fehler:", err);
        })
        .then(function () {
          zeigeSpinner.value = false;
          emit("next-page", { pageIndex: 2 });
        });
    }

    async function nextPage() {
      Logger.debug("next-page");

      eingabenSindValide().then((isValid) => {
        if (isValid) {
          anfragePruefenUndWeiter();
        }
      });
    }

    watch(
      () => piniaStore.state.tarifinformationen.zahlungsweise,
      (neu, alt) => {
        if (komponenteIstAktiv && neu !== alt) {
          Logger.debug("WATCH (IhreDaten>zahlungsweise): ZW hat sich geändert.");
          if (
            Number(piniaStore.state.tarifinformationen.zahlungsweise) > 1 &&
            piniaStore.state.zahlungsdaten.zahlungsart !== "debit"
          ) {
            Logger.debug("WATCH (IhreDaten>zahlungsweise): Auf Lastschrift umstellen...");
            piniaStore.state.zahlungsdaten.zahlungsart = "debit";
          }
        }
      },
    );

    watch(
      () => piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].gemZahlungsweise,
      (beitragNeu, beitragAlt) => {
        Logger.debug("WATCH (IhreDaten>zahlbeitrag):\nbeitragNeu:" + beitragNeu + "\nbeitragAlt:" + beitragAlt);
        if (
          komponenteIstAktiv &&
          beitragAlt > 0 &&
          beitragNeu !== beitragAlt &&
          (beitragsaenderungDurchSchadenanzahl || beitragsaenderungDurchHugMitgliedschaft)
        ) {
          Logger.debug("WATCH (IhreDaten>zahlbeitrag): Beitrag hat sich geändert.");
          if (beitragsaenderungDurchSchadenanzahl) {
            modalBeitragsaenderung.titel = "Vereinbarung Schadenfreiheitsrabatt";
            modalBeitragsaenderung.text = "Durch die Änderung der Schadenanzahl";
            beitragsaenderungDurchSchadenanzahl = false;
          } else if (beitragsaenderungDurchHugMitgliedschaft) {
            modalBeitragsaenderung.titel = "Mitgliedschaft Haus & Grund-Verein";
            modalBeitragsaenderung.text = "Durch die Änderung der Mitgliedschaft in einem Haus & Grund-Verein";
            beitragsaenderungDurchHugMitgliedschaft = false;
          }
          modalBeitragsaenderung.text +=
            " ändert sich Ihr Beitrag von " + formatCurrency(beitragAlt) + " auf " + formatCurrency(beitragNeu);
          Logger.debug(modalBeitragsaenderung.text);
          wechsleSichtbarkeit("modalBeitragsaenderung");
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.vorschaedenAnzahl,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          beitragsaenderungDurchSchadenanzahl = true;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.hugMitglied,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          beitragsaenderungDurchHugMitgliedschaft = true;
        }
      },
    );

    onActivated(() => {
      komponenteIstAktiv = true;
      zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
    });

    onDeactivated(() => {
      komponenteIstAktiv = false;
    });

    return {
      piniaStore,
      zeigeFrageHugNachlass,
      wechsleSichtbarkeit,
      istSichtbar,
      hatMitversichertePerson,
      nextPage,
      fehler,
      formatCurrency,
      modalBeitragsaenderung,
      isHaeuslicheGemeinschaft,
      antragstellerRef,
      zahlungsdatenRef,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="2" @goto-page="$emit('goto-page', $event)"></Steps>

  <section class="js-step-3">
    <antragsteller ref="antragstellerRef" :fehler="fehler"></antragsteller>

    <vertragsunterlagen-digital :fehler="fehler" />

    <!-- ### Mitversicherter Lebenspartner (MVP) ### -->
    <div v-if="hatMitversichertePerson()">
      <div class="o-grid u-mar-t-1">
        <div class="o-grid__cell o-grid__cell--4-1024 o-grid__cell--12-360">
          <h2 v-if="!piniaStore.state.isMakler" class="u-form-heading">Mitversicherter Lebenspartner</h2>
          <h2 v-else class="u-form-heading">Mitversicherte Person</h2>
        </div>
      </div>
      <!-- Art Partnerschaft -->
      <zeile>
        <titel-zelle>Art der Partnerschaft</titel-zelle>
        <zelle>
          <select
            id="typeOfPartnership"
            name="typeOfPartnership"
            v-model.number="piniaStore.state.personendaten.mitversichertePerson.artDerPartnerschaft"
            data-cy="artDerPartnerschaft"
            class="c-form-control-select"
            :required="hatMitversichertePerson()"
            :aria-required="hatMitversichertePerson()"
            aria-label="Art der Partnerschaft"
          >
            <option selected value="0">Bitte wählen</option>
            <option value="1">Ehe/eingetragene Lebenspartnerschaft</option>
            <option value="2">Häusliche Gemeinschaft</option>
          </select>
          <validierungs-fehler :fehler-text="fehler.typeOfPartnership" :reduzierter-abstand="true" />
        </zelle>
      </zeile>

      <!-- MVP: Vorname und Name -->
      <div v-if="isHaeuslicheGemeinschaft">
        <zeile>
          <titel-zelle>Vorname und Name</titel-zelle>
          <zelle :breite="3">
            <input
              type="text"
              id="partnerFirstname"
              name="partnerFirstname"
              v-model.lazy.trim="piniaStore.state.personendaten.mitversichertePerson.vorname"
              data-cy="partnerVorname"
              class="c-form-control__input"
              placeholder="Vorname"
              minlength="2"
              maxlength="40"
              :required="isHaeuslicheGemeinschaft"
              :aria-required="isHaeuslicheGemeinschaft"
              aria-label="Vorname des Partners"
              data-fehlermeldung="Bitte geben den Vornamen ein (2-40 Zeichen)."
            />
            <validierungs-fehler :fehler-text="fehler.partnerFirstname" />
          </zelle>
          <zelle :breite="3">
            <input
              type="text"
              id="partnerLastname"
              name="partnerLastname"
              v-model.lazy.trim="piniaStore.state.personendaten.mitversichertePerson.nachname"
              data-cy="partnerNachname"
              class="c-form-control__input"
              placeholder="Name"
              minlength="2"
              maxlength="40"
              :required="isHaeuslicheGemeinschaft"
              :aria-required="isHaeuslicheGemeinschaft"
              aria-label="Nachname des Partners"
              data-parsley-combined-name-length="40"
              data-fehlermeldung="Bitte geben den Nachnamen ein (2-40 Zeichen)."
            />
            <validierungs-fehler :fehler-text="fehler.partnerLastname" />
          </zelle>
        </zeile>

        <zeile>
          <titel-zelle>Geburtsdatum</titel-zelle>
          <zelle :breite="3">
            <input
              type="date"
              id="partnerBirthdate"
              name="partnerBirthdate"
              v-model.lazy.trim="piniaStore.state.personendaten.mitversichertePerson.geburtsdatum"
              data-cy="partnerGeburtsdatum"
              class="c-form-control__input"
              placeholder="TT.MM.JJJJ"
              title="Geburtsdatum im Format TT.MM.JJJJ"
              minlength="10"
              maxlength="10"
              pattern="[0-9]{2}.[0-9]{2}.[0-9]{4}"
              :required="isHaeuslicheGemeinschaft"
              :aria-required="isHaeuslicheGemeinschaft"
              aria-label="Geben Sie das Geburtsdatum im Format TT.MM.JJJJ ein"
              data-checkalter18bis100="true"
            />
            <validierungs-fehler :fehler-text="fehler.partnerBirthdate" />
          </zelle>
        </zeile>
      </div>
    </div>

    <zahlungsdaten ref="zahlungsdatenRef" :fehler="fehler" />

    <vorversicherung :fehler="fehler" />

    <vorschaeden :fehler="fehler" :zeige-schadenart="piniaStore.state.tarifinformationen.hundehalterhaftpflicht" />

    <hug-mitgliedschaft v-if="zeigeFrageHugNachlass" :fehler="fehler" />

    <!-- Absenden -->
    <zeile>
      <zelle :offset="4" :breite="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage()"
          data-cy="submitIhreDaten"
        >
          Zusammenfassung und Abschluss
        </button>
      </zelle>
    </zeile>
  </section>

  <modal
    v-if="istSichtbar('modalBeitragsaenderung')"
    v-bind="modalBeitragsaenderung"
    @closeModal="wechsleSichtbarkeit('modalBeitragsaenderung')"
  />
</template>
